import { useState, useEffect, use } from "react";
import { getDoc, doc } from "firebase/firestore";
import { useFirestore } from "../hooks/use-firebase";
import Page from "../components/page/page";
import { useUserContext } from "../hooks/use-user";
import { useInventoryContext } from "../hooks/use-inventory";
import { useListingContext } from "../hooks/use-listings";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Box, Inbox, Package, TrendingUp, Warehouse } from "lucide-react";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "../hooks/use-mobile";
import { EarningsChart } from "../components/widgets/earnings-chart";
import { getFeatureLimits } from "../lib/subscription-limits";
import { UpgradePrompt } from "../components/ui/upgrade-prompt";

export const Home = () => {
  const { user, subscription } = useUserContext()
  const { inventory } = useInventoryContext()
  const { listings, sales } = useListingContext()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const limits = getFeatureLimits(subscription)

  const totalValue = inventory.reduce((sum, item) => sum + (Number(item.value) * item.quantity), 0)
  const totalItems = inventory.reduce((sum, item) => sum + item.quantity, 0)
  const activeListings = listings.filter(l => l.status === 'active').length
  const soldListings = listings.filter(l => l.status === 'sold').length

  return (
    <Page>
      <div className="p-6 space-y-6">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-sumoPrimary via-sumoAmber to-sumoOrange rounded-lg p-8 shadow-yellow text-sumoNavy">
          <h1 className="text-3xl font-bold mb-2">
            Welcome back, {user?.firstName || 'Friend'}! 👋
          </h1>
          <p className="text-lg opacity-90">
            Here's what's happening with your inventory today
          </p>
        </div>

        {/* Subscription Usage Indicator */}
        {limits?.maxInventoryItems && limits.maxInventoryItems !== null && (
          <Card className={`dark:bg-neutral800 dark:border-neutral700 ${inventory.length >= limits.maxInventoryItems
            ? 'border-error500 dark:border-error600 bg-error50 dark:bg-error900/20'
            : inventory.length >= limits.maxInventoryItems * 0.8
              ? 'border-warning500 dark:border-warning600 bg-warning50 dark:bg-warning900/20'
              : ''
            }`}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between dark:text-neutral100">
                <span>Subscription: {subscription?.charAt(0).toUpperCase() + subscription?.slice(1)} Plan</span>
                {inventory.length >= limits.maxInventoryItems * 0.8 && (
                  <Button variant="primary" size="sm" onClick={() => window.location.href = '/#pricing'}>
                    Upgrade
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-neutral600 dark:text-neutral400">Inventory Items</span>
                  <span className={`font-semibold ${inventory.length >= limits.maxInventoryItems
                    ? 'text-error600 dark:text-error400'
                    : inventory.length >= limits.maxInventoryItems * 0.8
                      ? 'text-warning600 dark:text-warning400'
                      : 'text-neutral700 dark:text-neutral300'
                    }`}>
                    {inventory.length} / {limits.maxInventoryItems}
                  </span>
                </div>
                <div className="w-full bg-neutral200 dark:bg-neutral700 rounded-full h-3 overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 ${inventory.length >= limits.maxInventoryItems
                      ? 'bg-error500'
                      : inventory.length >= limits.maxInventoryItems * 0.8
                        ? 'bg-warning500'
                        : 'bg-success500'
                      }`}
                    style={{ width: `${Math.min((inventory.length / limits.maxInventoryItems) * 100, 100)}%` }}
                  />
                </div>
              </div>
              {inventory.length >= limits.maxInventoryItems && (
                <p className="text-sm text-error700 dark:text-error300 font-medium">
                  ⚠️ You've reached your plan limit. Upgrade to add more items!
                </p>
              )}
              {inventory.length >= limits.maxInventoryItems * 0.8 && inventory.length < limits.maxInventoryItems && (
                <p className="text-sm text-warning700 dark:text-warning300">
                  You're approaching your plan limit ({limits.maxInventoryItems - inventory.length} slots remaining)
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Earnings Chart */}
        {limits?.canViewAnalytics ? (
          <EarningsChart sales={sales} inventory={inventory} />
        ) : (
          <UpgradePrompt
            feature="Earnings Analytics"
            description="Track your revenue, costs, and profit margins over time with beautiful interactive charts. See your business performance at a glance."
            requiredTier="Premium"
            compact
          />
        )}

        {/* Stats Grid */}
        <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2 lg:grid-cols-4'}`}>
          <Card className="dark:bg-neutral800 dark:border-neutral700 hover:border-sumoPrimary cursor-pointer" onClick={() => navigate('/inventory')}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-neutral600 dark:text-neutral400">Total Items</CardTitle>
              <Package className="h-5 w-5 text-sumoPrimary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-neutral900 dark:text-neutral100">{totalItems}</div>
              <p className="text-xs text-neutral500 dark:text-neutral400 mt-1">
                Across {inventory.length} unique products
              </p>
            </CardContent>
          </Card>

          <Card className="dark:bg-neutral800 dark:border-neutral700 hover:border-sumoTeal cursor-pointer" onClick={() => navigate('/inventory')}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-neutral600 dark:text-neutral400">Inventory Value</CardTitle>
              <TrendingUp className="h-5 w-5 text-sumoTeal" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-neutral900 dark:text-neutral100">${totalValue.toFixed(2)}</div>
              <p className="text-xs text-neutral500 dark:text-neutral400 mt-1">
                Total estimated value
              </p>
            </CardContent>
          </Card>

          <Card className="dark:bg-neutral800 dark:border-neutral700 hover:border-warning500 cursor-pointer" onClick={() => navigate('/listings')}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-neutral600 dark:text-neutral400">Active Listings</CardTitle>
              <Inbox className="h-5 w-5 text-warning500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-neutral900 dark:text-neutral100">{activeListings}</div>
              <p className="text-xs text-neutral500 dark:text-neutral400 mt-1">
                Currently listed for sale
              </p>
            </CardContent>
          </Card>

          <Card className="dark:bg-neutral800 dark:border-neutral700 hover:border-success500 cursor-pointer" onClick={() => navigate('/listings')}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-neutral600 dark:text-neutral400">Sold Items</CardTitle>
              <Box className="h-5 w-5 text-success500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-neutral900 dark:text-neutral100">{soldListings}</div>
              <p className="text-xs text-neutral500 dark:text-neutral400 mt-1">
                Successfully sold
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="dark:bg-neutral800 dark:border-neutral700">
          <CardHeader>
            <CardTitle className="dark:text-neutral100">Quick Actions</CardTitle>
            <CardDescription className="dark:text-neutral400">Get started with common tasks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className={`grid gap-3 ${isMobile ? 'grid-cols-1' : 'grid-cols-2 lg:grid-cols-3'}`}>
              <Button
                variant="primary"
                className="w-full justify-start"
                onClick={() => navigate('/inventory')}
              >
                <Package className="mr-2" width={18} height={18} />
                Add Inventory Item
              </Button>
              <Button
                variant="outlinePrimary"
                className="w-full justify-start"
                onClick={() => navigate('/listings')}
              >
                <Inbox className="mr-2" width={18} height={18} />
                Create Listing
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate('/inventory')}
              >
                <Warehouse className="mr-2" width={18} height={18} />
                View Inventory
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Page>
  );
}
